/**
 * The one shape every screen reads sign-in state through.
 *
 * Screens never touch MSAL directly. They read a `Session`, which says which
 * of the backend's two modes is in force and, under `entra`, whether someone
 * is signed in. Keeping MSAL behind this seam is what lets the mock and
 * unconfigured paths render without an MSAL instance at all, and what keeps a
 * future form or reconciliation screen from importing MSAL to make a request.
 */
import { createContext, useContext } from "react";

import type { AuthConfigOut } from "../api/public";

export type Session =
  /** `/auth/config` has not answered yet. */
  | { kind: "loading" }
  /** `/auth/config` failed. The backend is down or the proxy is not pointed at it. */
  | { kind: "error"; message: string }
  /** `AUTH_MODE=mock`. The backend trusts a header; there is nothing to sign in to. */
  | { kind: "mock"; config: AuthConfigOut }
  /** `AUTH_MODE=entra` but no client id, so MSAL cannot be initialised. */
  | { kind: "unconfigured"; config: AuthConfigOut }
  /** MSAL is initialised; nobody is signed in. */
  | { kind: "signed-out"; config: AuthConfigOut; signIn: () => Promise<void> }
  /** A redirect is in flight or its response is being processed. */
  | { kind: "signing-in"; config: AuthConfigOut }
  /** A staff account is signed in. `upn` is MSAL's `username` (`preferred_username`). */
  | {
      kind: "signed-in";
      config: AuthConfigOut;
      upn: string;
      /** A bearer token for the API scope, silently when possible, by redirect otherwise. */
      getAccessToken: () => Promise<string>;
      signOut: () => Promise<void>;
    };

export const SessionContext = createContext<Session>({ kind: "loading" });

export function useSession(): Session {
  return useContext(SessionContext);
}

/** A function every authenticated call (submit, upload, reconciliation) can
 * await for a bearer token - `undefined` under `mock`, where the backend
 * trusts a header instead. `null` when the session is not yet in a state that
 * can make a request at all (loading, error, signing-in, unconfigured,
 * signed-out): a caller checks for `null` and renders `SessionGate`'s own
 * gate messages instead of trying to call anything. */
export type TokenGetter = () => Promise<string | undefined>;

export function tokenGetterFor(session: Session): TokenGetter | null {
  if (session.kind === "mock") return async () => undefined;
  if (session.kind === "signed-in") return session.getAccessToken;
  return null;
}
